import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, Clock } from "lucide-react";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { getInsight } from "@/data/insights";
import { COMPANY } from "@/data/site";

const InsightDetail = () => {
  const { slug } = useParams();
  const article = getInsight(slug);

  useEffect(() => {
    document.title = article ? `${article.title} — Click World Online` : "Insights — Click World Online";
  }, [article]);

  if (!article) {
    return (
      <section className="bg-navy-deep min-h-screen flex items-center">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-40 text-center w-full">
          <h1 className="font-display text-4xl md:text-6xl font-extrabold text-mist tracking-tight">Article not found.</h1>
          <Link to="/insights" data-testid="insight-not-found-back" className="mt-8 inline-flex items-center gap-2 text-electric font-semibold">
            Back to Insights <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="relative bg-navy-deep grain overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-25" aria-hidden="true" />
        <div className="absolute -top-32 right-0 w-[500px] h-[400px] bg-brand/15 blur-[130px] rounded-full" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-6 pt-40 pb-16 lg:pt-52 lg:pb-20">
          <Link to="/insights" data-testid="article-back-link" className="inline-flex items-center gap-2 text-slatec hover:text-electric transition-colors text-sm font-semibold mb-10">
            <ArrowLeft size={16} /> All Insights
          </Link>
          <div className="flex flex-wrap items-center gap-4 text-xs uppercase tracking-[0.2em]">
            <span className="text-electric font-semibold">{article.category}</span>
            <span className="text-mist/40">{article.date}</span>
            <span className="text-mist/40 flex items-center gap-1.5">
              <Clock size={12} /> {article.readTime}
            </span>
          </div>
          <h1 data-testid="article-title" className="mt-6 font-display font-extrabold tracking-tighter leading-[1.05] text-4xl md:text-5xl lg:text-6xl text-mist">
            {article.title}
          </h1>
          <p className="mt-6 text-mist/50 text-sm">
            By <span className="text-mist/80 font-medium">{COMPANY.name}</span> — {COMPANY.address2}
          </p>
        </div>
      </section>

      <article className="bg-navy-deep" data-testid="article-body">
        <div className="max-w-3xl mx-auto px-6 pb-24 lg:pb-32">
          <div className="space-y-6">
            {article.intro.map((p, i) => (
              <p key={i} className={`leading-relaxed ${i === 0 ? "text-lg md:text-xl text-mist/90" : "text-base md:text-lg text-slatec"}`}>
                {p}
              </p>
            ))}
          </div>

          {article.sections.map((s, i) => (
            <Reveal key={i} className="mt-16 lg:mt-20">
              <section data-testid={`article-section-${i}`}>
                <h2 className="font-display text-2xl lg:text-4xl font-extrabold tracking-tight text-mist leading-tight">
                  {s.num && <span className="text-electric mr-3">{s.num}.</span>}
                  {s.heading}
                </h2>
                <div className="mt-6 space-y-5">
                  {s.paragraphs?.map((p, j) => (
                    <p key={j} className="text-slatec leading-relaxed text-base md:text-lg">{p}</p>
                  ))}
                  {s.leadIn && <p className="text-mist/85 leading-relaxed text-base md:text-lg font-medium">{s.leadIn}</p>}
                  {s.list && (
                    <ul className="space-y-3 py-2">
                      {s.list.map((item) => (
                        <li key={item} className="flex items-start gap-3 text-slatec">
                          <Check size={18} className="text-electric shrink-0 mt-1" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {s.after?.map((p, j) => (
                    <p key={j} className="text-slatec leading-relaxed text-base md:text-lg">{p}</p>
                  ))}
                </div>
              </section>
            </Reveal>
          ))}

          <Reveal className="mt-20">
            <div className="relative bg-navy-light border border-electric/20 rounded-3xl p-8 lg:p-12 overflow-hidden grain">
              <div className="absolute inset-0 dot-grid opacity-15" aria-hidden="true" />
              <div className="relative">
                <h2 className="font-display text-2xl lg:text-3xl font-extrabold tracking-tight text-mist">
                  {article.outro.heading}
                </h2>
                <div className="mt-5 space-y-5">
                  {article.outro.paragraphs.map((p, i) => (
                    <p key={i} className="text-slatec leading-relaxed">{p}</p>
                  ))}
                </div>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    data-testid="article-cta-contact"
                    className="inline-flex items-center gap-2 bg-brand hover:bg-electric hover:text-navy-deep text-white font-semibold px-8 py-4 rounded-full transition-colors duration-300"
                  >
                    Let's Grow Your Brand <ArrowUpRight size={18} />
                  </Link>
                  <Link
                    to="/services"
                    data-testid="article-cta-services"
                    className="inline-flex items-center gap-2 border border-white/20 text-mist hover:border-electric hover:text-electric font-semibold px-8 py-4 rounded-full transition-colors duration-300"
                  >
                    Explore Our Services
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </article>
      <CTASection />
    </>
  );
};

export default InsightDetail;
